<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import copyToClipBoard from '@utils/copy-to-clipboard'
import { siteUrl } from '@/config/site'
import SocialShare from '@components/social-share'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMessages } from '@composables/use-messages'

const props = defineProps({
    channel: {
        type: Object,
        default: () => {},
    },
    // Defaults are the /radio channel-tile menu (channel-detail-tile.vue), which passes
    // neither: it keeps View Channel and gets no settings links. The channel-page hero
    // inverts both — the same way the artist hero passes `hasViewArtistOption: false`.
    hasViewChannelOption: {
        type: Boolean,
        default: true,
    },
    hasSettingsLinks: {
        type: Boolean,
        default: false,
    },
})

const appearanceStore = useAppearanceStore()
const router = useRouter()
const getMessageText = useMessages()

const url = computed(() => {
    // Router 4 throws on a falsy required param, and this resolves during render.
    if (!props.channel.id) return ''

    const href = router.resolve({ name: 'channel', params: { channelId: props.channel.id } }).href
    return siteUrl(href)
})

function copyLinkToClipBoard() {
    copyToClipBoard(url.value)
    appearanceStore.handleNotification({
        message: getMessageText('LINK_COPIED_CLIPBOARD'),
        type: 'success',
    })
}

function goToAudioQuality() {
    return router.push({ name: 'settings/audio-quality' })
}

function goToExternalPlayer() {
    return router.push({ name: 'settings/external-player' })
}
</script>

<template>
    <ul class="channel-options">
        <li
            v-if="hasViewChannelOption"
            class="channel-options-item"
            @click="router.push({ name: 'channel', params: { channelId: channel.id } })"
        >
            <BaseButton
                icon="eye-open"
                size="medium"
                button-type="transparent"
                @click="() => {}"
            >&nbsp; View Channel</BaseButton>
        </li>
        <!-- The settings cluster and the separator that closes it appear together or not
             at all — one condition over the group, so a fourth link cannot be added and
             left ungated. -->
        <template v-if="hasSettingsLinks">
            <li
                class="channel-options-item"
                @click="goToAudioQuality"
            >
                <BaseButton
                    icon="headphones"
                    size="medium"
                    button-type="transparent"
                    @click="() => {}"
                >&nbsp;Audio Quality</BaseButton>
            </li>
            <li
                class="channel-options-item"
                @click="goToExternalPlayer"
            >
                <BaseButton
                    icon="tv-speaker"
                    size="medium"
                    button-type="transparent"
                    @click="() => {}"
                >&nbsp;Play in External Player</BaseButton>
            </li>
            <div class="separator" />
        </template>
        <li class="channel-options-item">
            <BaseFavourites
                v-if="channel"
                :channel="channel"
                size="medium"
                button-type="transparent"
            >
                <template #main>
                    <span>Add {{ channel.title }} to Favorites</span>
                </template>
                <template #alt>
                    <span>Remove {{ channel.title }} from Favorites</span>
                </template>
            </BaseFavourites>
        </li>
        <h6 class="share-title">Share</h6>
        <div class="separator" />
        <li class="channel-options-item">
            <BaseButton
                icon="logo-facebook"
                size="medium"
                button-type="transparent"
            >
                <SocialShare
                    :url="url"
                    type="facebook"
                />
            </BaseButton>
        </li>
        <li class="channel-options-item">
            <BaseButton
                icon="logo-x"
                size="medium"
                button-type="transparent"
            >
                <SocialShare
                    :url="url"
                    :custom-text="`${channel.title} on @friskyradio #feelinfrisky`"
                    type="twitter"
                />
            </BaseButton>
        </li>
        <li
            class="channel-options-item"
            @click="copyLinkToClipBoard"
        >
            <BaseButton
                icon="link"
                size="medium"
                button-type="transparent"
                @click="() => {}"
            >&nbsp;Copy link</BaseButton>
        </li>
    </ul>
</template>

<style lang="scss" scoped>
@import '@design';

.channel-options {
    padding: 0;
    margin: 0;
    text-align: left;
    list-style: none;

    &-item {
        display: flex;
        align-items: center;
        height: 44px;
        padding: 0 $s-m;
    }
}

.separator {
    position: relative;
    display: block;
    width: 100%;
    height: 1px;
    background: $c-surface-overlay;
}

.share-title {
    @extend %contentTitleM;

    margin: $s-s 0 $s-s $s-xl;
}
</style>
